import { BarChart, Bell, BookOpenCheck, Calendar, DollarSign, HandPlatter, Home, LogOut, Scissors, Settings, ShoppingBasket, User, Users,} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { logout } from "../../store/slices/authSlice";
import { RootState } from "../../store/store";
import { Toast } from "../common/Toast";

const DashboardLayout = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const user = useSelector((state: RootState) => state.auth.user);

  const superAdmin = user?.role === "admin_super";
  const support = user?.role === "staff_support";

  const handleLogout = () => {
    localStorage.clear();
    dispatch(logout());
    navigate("/login");
  };

  const navigationItems = [
    { name: "Dashboard", icon: Home, path: "/" },
    ...(superAdmin
      ? [
        { name: "Customers", icon: Users, path: "/customers" },
        { name: "Providers", icon: HandPlatter, path: "/providers" },
        { name: "Services", icon: ShoppingBasket, path: "/services" },
        { name: "Verifications", icon: BookOpenCheck, path: "/verifications" },
        { name: "Bookings", icon: Calendar, path: "/bookings" },
        { name: "Finances", icon: DollarSign, path: "/finances" },
        { name: "Reports", icon: BarChart, path: "/reports" },
        { name: "Settings", icon: Settings, path: "/settings" },
      ]
      : []),
    ...(support
      ? [
        { name: "Customers", icon: Users, path: "/customers" },
        { name: "Bookings", icon: Calendar, path: "/bookings" },
        { name: "Services", icon: Scissors, path: "/services" },
      ]
      : []),
  ];

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-[whitesmoke] shadow-sm">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Left: Logo + Nav items */}
            <div className="flex items-center">
              {/* Logo */}
              <div className="flex-shrink-0 flex items-center">
                <img
                  src={require("assets/logos/secondary.png")}
                  alt="Primary Logo"
                  className="h-16 w-18 text-primary-600"
                />
              </div>


            </div>
            <div className="flex items-center">
              {/* Nav items */}
              <div className="ml-4 flex items-center space-x-1">
                {navigationItems.map((item) => {
                  const active = isActive(item.path);
                  return (
                    <div key={item.name} className="relative group">
                      <button
                        onClick={() => navigate(item.path)}
                        className={`inline-flex items-center px-2 pt-1 border-b-2 text-sm font-small h-16 transition-colors duration-150
                          ${active
                            ? "border-primary-600 text-primary-600"
                            : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
                          }`}
                      >
                        <item.icon className="h-5 w-5 flex-shrink-0" />
                        <span className="hidden md:inline ml-1">{item.name}</span>
                      </button>

                      {/* Tooltip: only visible below md */}
                      <div className="md:hidden absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-50">
                        {item.name}
                        <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center space-x-1">
              {/* Notifications */}
              <div className="relative group">
                <button
                  onClick={() => navigate("/notifications")}
                  className="p-2 rounded-full text-gray-400 hover:text-gray-500 relative"
                >
                  <Bell className="h-6 w-6" />
                  <span className="absolute top-1 right-1 block h-2 w-2 rounded-full bg-red-400 ring-2 ring-white" />
                </button>
                <div className="md:hidden absolute left-1/2 -translate-x-1/2 top-full mt-1 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-50">
                  Notifications
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45" />
                </div>
              </div>

              {/* Profile */}
              <div className="relative group">
                <button
                  onClick={() => navigate("/profile")}
                  className="p-2 rounded-full text-gray-400 hover:text-gray-500"
                >
                  <User className="h-6 w-6" />
                </button>
                <div className="md:hidden absolute right-0 top-full mt-1 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-50">
                  Profile
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45" />
                </div>
              </div>

              {/* Logout */}
              <div className="relative group">
                <button
                  onClick={handleLogout}
                  className="p-2 rounded-full text-gray-400 hover:text-gray-500"
                >
                  <LogOut className="h-6 w-6" />
                </button>
                <div className="md:hidden absolute right-0 top-full mt-1 px-2 py-1 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-50">
                  Logout
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-8xl mx-auto py-6 sm:px-6 lg:px-8">
        <Outlet />
      </main>

      <Toast />
    </div>
  );
};

export default DashboardLayout;